import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, BottomNavigation, BottomNavigationAction,
  Avatar, Menu, MenuItem, Divider, useMediaQuery, useTheme, Chip, Switch,
  Collapse, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert,
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Park as ParkIcon, Map as MapIcon,
  People as PeopleIcon, Upload as UploadIcon, Menu as MenuIcon,
  Logout as LogoutIcon, Person as PersonIcon, ChevronLeft,
  History as HistoryIcon, DarkMode, LightMode,
  Assignment as AssignmentIcon, Assessment as AssessmentIcon,
  NewReleases as NewReleasesIcon, AssignmentTurnedIn,
  ExpandLess, ExpandMore, Forest as ForestIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { VERSION_DISPLAY } from '../data/version';
import NotificationsBell from './NotificationsBell';
import api from '../services/api';

const KUNDE_NAME = process.env.REACT_APP_KUNDE_NAME || '';

const DRAWER_WIDTH = 250;

// Navigation gruppiert nach Workflow-Schwerpunkten.
// Jede Gruppe ist im UI einklappbar (Toggle, default offen).
const navGroups: { id: string; label: string; items: { path: string; label: string; icon: React.ReactNode; roles: string[] }[] }[] = [
  {
    id: 'ueberblick',
    label: 'Überblick',
    items: [
      { path: '/dashboard',      label: 'Dashboard',      icon: <DashboardIcon />,    roles: ['admin', 'erfasser', 'betrachter', 'bauhof'] },
      { path: '/meine-aufgaben', label: 'Aufgaben', icon: <AssignmentTurnedIn />, roles: ['admin', 'erfasser', 'betrachter', 'bauhof'] },
    ],
  },
  {
    id: 'baumdaten',
    label: 'Baumdaten',
    items: [
      { path: '/map',    label: 'Karte',         icon: <MapIcon />,    roles: ['admin', 'erfasser', 'betrachter', 'bauhof'] },
      { path: '/trees',  label: 'Baumkataster',  icon: <ParkIcon />,   roles: ['admin', 'erfasser', 'betrachter', 'bauhof'] },
      { path: '/flaechen', label: 'Waldflächen', icon: <ForestIcon />, roles: ['admin', 'erfasser', 'betrachter', 'bauhof'] },
      { path: '/import', label: 'Datenimport',   icon: <UploadIcon />, roles: ['admin'] },
    ],
  },
  {
    id: 'planung',
    label: 'Planung & Reports',
    items: [
      { path: '/leistungsverzeichnis', label: 'Leistungsverzeichnis', icon: <AssignmentIcon />,  roles: ['admin', 'erfasser'] },
      { path: '/auswertung',           label: 'Auswertung',           icon: <AssessmentIcon />,  roles: ['admin', 'erfasser'] },
    ],
  },
  {
    id: 'verwaltung',
    label: 'Verwaltung',
    items: [
      { path: '/users',         label: 'Benutzer',      icon: <PeopleIcon />,     roles: ['admin'] },
      { path: '/activity',      label: 'Historie',      icon: <HistoryIcon />,    roles: ['admin', 'erfasser', 'betrachter', 'bauhof'] },
      { path: '/release-notes', label: 'Release Notes', icon: <NewReleasesIcon />,roles: ['admin', 'erfasser', 'betrachter', 'bauhof'] },
    ],
  },
];

// Flat-Liste aller Items (für Title-Lookup, Mobile-Nav etc)
const allNavItems = navGroups.flatMap(g => g.items);

const roleLabels: Record<string, string> = {
  admin: 'Administrator',
  erfasser: 'Erfasser',
  betrachter: 'Betrachter',
};

export default function AppLayout() {
  const { user, logout, isAdmin } = useAuth();
  const { darkMode, toggleDarkMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Passwort ändern (Self-Service für den eingeloggten Nutzer)
  const [pwDialog, setPwDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const openPwDialog = () => {
    setAnchorEl(null);
    setPwForm({ current: '', next: '', confirm: '' });
    setPwError(''); setPwSuccess('');
    setPwDialog(true);
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (!pwForm.current || !pwForm.next) { setPwError('Bitte alle Felder ausfüllen.'); return; }
    if (pwForm.next.length < 8) { setPwError('Das neue Passwort muss mindestens 8 Zeichen lang sein.'); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError('Die neuen Passwörter stimmen nicht überein.'); return; }
    if (pwForm.next === pwForm.current) { setPwError('Das neue Passwort muss sich vom aktuellen unterscheiden.'); return; }
    setPwSaving(true);
    try {
      await api.put('/auth/change-password', { currentPassword: pwForm.current, newPassword: pwForm.next });
      setPwSuccess('Passwort erfolgreich geändert.');
      setPwForm({ current: '', next: '', confirm: '' });
      setTimeout(() => setPwDialog(false), 1500);
    } catch (e: any) {
      setPwError(e.response?.data?.error || 'Passwort konnte nicht geändert werden.');
    } finally {
      setPwSaving(false);
    }
  };

  // Nach Rolle filterte Navigation
  const role = user?.role || '';
  const visibleGroups = navGroups
    .map(g => ({ ...g, items: g.items.filter(i => i.roles.includes(role)) }))
    .filter(g => g.items.length > 0);
  // Flat-Liste für Title-Lookup und Mobile-Bottom-Nav
  const filteredNav = visibleGroups.flatMap(g => g.items);
  const mobileNav = filteredNav.slice(0, 4);

  // Toggle-State für Gruppen, in localStorage persistiert
  const GROUPS_KEY = 'baumwaechter.sidebar.groups';
  const [groupsOpen, setGroupsOpen] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(GROUPS_KEY);
      if (raw) return JSON.parse(raw);
    } catch {/* ignore */}
    // Default: alle offen
    return Object.fromEntries(navGroups.map(g => [g.id, true]));
  });
  const toggleGroup = (id: string) => {
    setGroupsOpen(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(GROUPS_KEY, JSON.stringify(next)); } catch {/* ignore */}
      return next;
    });
  };

  const handleLogout = () => { setAnchorEl(null); logout(); navigate('/login'); };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <img src="/logo.png" alt="Baumwächter" style={{ height: 48, width: '100%', objectFit: 'contain' }} />
          <Box sx={{ flex: 1 }} />
          {isMobile && <IconButton onClick={() => setDrawerOpen(false)}><ChevronLeft /></IconButton>}
        </Box>
        {KUNDE_NAME && (
            <Typography sx={{
              color: '#3d6b4a', fontWeight: 800, fontSize: '1.1rem',
              lineHeight: 1.2, letterSpacing: 0.2, textAlign: 'center',
              mt: 1.25, pb: 1, borderBottom: '2px solid rgba(74,124,89,0.2)',
              width: '100%',
            }}>
              {KUNDE_NAME}
            </Typography>
          )}
          <Box
             onClick={() => { navigate('/release-notes'); if (isMobile) setDrawerOpen(false); }}
             title="Release Notes anzeigen"
             sx={{
               display: 'inline-block', mt: 1,
               px: 1.25, py: 0.3, borderRadius: 999,
               bgcolor: 'rgba(212,168,67,0.15)',
               cursor: 'pointer',
               '&:hover': { bgcolor: 'rgba(212,168,67,0.28)' },
             }}
           >
             <Typography sx={{
               color: '#b5923a', fontWeight: 700, fontSize: '0.62rem',
               letterSpacing: 1.5, textTransform: 'uppercase',
             }}>
               {VERSION_DISPLAY}
             </Typography>
           </Box>
      </Box>

      {/* Navigation – nach Gruppen sortiert, jede Gruppe einklappbar */}
      <List sx={{ flex: 1, py: 1, overflowY: 'auto' }}>
        {visibleGroups.map((group, gi) => {
          const open = groupsOpen[group.id] ?? true;
          return (
            <React.Fragment key={group.id}>
              {/* Gruppen-Header: kleine ausgegraute Caps, mit Toggle-Icon */}
              <ListItemButton
                onClick={() => toggleGroup(group.id)}
                sx={{
                  px: 2, py: 0.6, mx: 1, mt: gi === 0 ? 0 : 1, mb: 0.3,
                  borderRadius: 1.5,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
                {open
                  ? <ExpandLess sx={{ fontSize: 16, color: 'text.disabled' }} />
                  : <ExpandMore sx={{ fontSize: 16, color: 'text.disabled' }} />}
              </ListItemButton>

              <Collapse in={open} timeout="auto" unmountOnExit>
                {group.items.map((item) => (
                  <ListItem key={item.path} disablePadding sx={{ px: 1, mb: 0.4 }}>
                    <ListItemButton
                      onClick={() => { navigate(item.path); if (isMobile) setDrawerOpen(false); }}
                      selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
                      sx={{
                        borderRadius: 2,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main', color: 'white',
                          '& .MuiListItemIcon-root': { color: 'white' },
                          '&:hover': { bgcolor: 'primary.dark' },
                        },
                      }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>

      {/* Dark Mode Toggle */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        {darkMode ? <DarkMode sx={{ fontSize: 20, color: 'text.secondary' }} /> : <LightMode sx={{ fontSize: 20, color: 'text.secondary' }} />}
        <Typography variant="body2" sx={{ flex: 1, fontSize: '0.82rem' }}>{darkMode ? 'Dark Mode' : 'Light Mode'}</Typography>
        <Switch size="small" checked={darkMode} onChange={toggleDarkMode} />
      </Box>

      {/* User Info Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.85rem' }}>
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{user?.name}</Typography>
            <Chip label={roleLabels[user?.role || '']} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100dvh', overflow: 'hidden', bgcolor: 'background.default' }}>
      {!isMobile && (
        <Drawer variant="persistent" open={drawerOpen}
          sx={{ width: drawerOpen ? DRAWER_WIDTH : 0, flexShrink: 0, transition: 'width 0.2s',
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', borderRight: '1px solid', borderColor: 'divider' } }}>
          {drawerContent}
        </Drawer>
      )}
      {isMobile && (
        <Drawer variant="temporary" open={drawerOpen} onClose={() => setDrawerOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawerContent}
        </Drawer>
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider', pt: 'env(safe-area-inset-top)' }}>
          <Toolbar>
            <IconButton edge="start" onClick={() => setDrawerOpen(!drawerOpen)} sx={{ mr: 2 }}><MenuIcon /></IconButton>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
              {filteredNav.find(n => location.pathname === n.path || location.pathname.startsWith(n.path + '/'))?.label || 'Baumwächter'}
            </Typography>
            <NotificationsBell />
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34, fontSize: '0.85rem' }}>{user?.name?.charAt(0).toUpperCase()}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
              <MenuItem disabled><PersonIcon sx={{ mr: 1 }} /> {user?.name}</MenuItem>
              <Divider />
              <MenuItem onClick={openPwDialog}><LockResetIcon sx={{ mr: 1 }} /> Passwort ändern</MenuItem>
              <MenuItem onClick={handleLogout}><LogoutIcon sx={{ mr: 1 }} /> Abmelden</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, minHeight: 0, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
          <Box sx={{ pb: isMobile ? 'calc(80px + env(safe-area-inset-bottom))' : 0 }}>
            <Outlet />
          </Box>
        </Box>
        {isMobile && (
          <BottomNavigation
            value={mobileNav.findIndex(n => location.pathname === n.path || location.pathname.startsWith(n.path + '/'))}
            onChange={(_, idx) => navigate(mobileNav[idx].path)}
            showLabels
            sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, borderTop: '1px solid', borderColor: 'divider', boxShadow: '0 -2px 10px rgba(0,0,0,0.05)', zIndex: 1200, height: 'auto', minHeight: 66, pb: 'env(safe-area-inset-bottom)' }}>
            {mobileNav.map((item) => <BottomNavigationAction key={item.path} label={item.label} icon={item.icon} sx={{ minWidth: 64, py: 1.2, '& .MuiBottomNavigationAction-label': { fontSize: '0.8rem', mt: 0.4, '&.Mui-selected': { fontSize: '0.82rem' } }, '& .MuiSvgIcon-root': { fontSize: 28 } }} />)}
          </BottomNavigation>
        )}
      </Box>

      {/* Passwort ändern */}
      <Dialog open={pwDialog} onClose={() => !pwSaving && setPwDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Passwort ändern</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {pwError && <Alert severity="error">{pwError}</Alert>}
            {pwSuccess && <Alert severity="success">{pwSuccess}</Alert>}
            <TextField label="Aktuelles Passwort" type="password" size="small" fullWidth autoComplete="current-password"
              value={pwForm.current} onChange={e => setPwForm({ ...pwForm, current: e.target.value })} />
            <TextField label="Neues Passwort" type="password" size="small" fullWidth autoComplete="new-password"
              helperText="Mindestens 8 Zeichen"
              value={pwForm.next} onChange={e => setPwForm({ ...pwForm, next: e.target.value })} />
            <TextField label="Neues Passwort wiederholen" type="password" size="small" fullWidth autoComplete="new-password"
              value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') handleChangePassword(); }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwDialog(false)} disabled={pwSaving}>Abbrechen</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={pwSaving}>Ändern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
